import AppSettings from 'Settings/AppSettings';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import { TPaginationParams } from '../../../Performance/analytics';

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

// List UI Models with pagination, sorting, search
export const fetchUIModelsApi = async (
  projectId: string,
  paginationParams: TPaginationParams,
  sortBy: string,
  sortOrder: string,
  searchTerm: string
) => {
  try {
    const response = await ServiceWrapper.doGet(
      `${api_base_url}guide/${projectId}/model-ui?page=${paginationParams.pageNumber}&limit=${paginationParams.pageSize}&sortBy=${sortBy}&sortOrder=${sortOrder}${searchTerm.length > 0 ? '&searchField=name&searchValue=' + searchTerm : ''}`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.log('Error', error);
    return null;
  }
};

// Delete UI Model
export const deleteUIModelApi = async (configureId: string, uiModelId: string) => {
  try {
    const response = await ServiceWrapper.doPost(
      `${api_base_url}guide/${configureId}/model-ui/delete`,
      { uimodel_ids: [uiModelId] },
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.log('Error', error);
    return null;
  }
};

// Promote UI Model (lightweight — no product builder map trigger)
export const promoteUIModelApi = async (configureId: string, uiModelId: string) => {
  try {
    const response = await ServiceWrapper.doPut(
      `${api_base_url}guide/${configureId}/model-ui/${uiModelId}/promote`,
      {},
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.log('Error', error);
    return null;
  }
};

// Create UI Model
export const createUIModelApi = async (configureId: string, formData: FormData) => {
  const response = await ServiceWrapper.doPost(
    `${api_base_url}guide/${configureId}/model-ui`,
    formData,
    null,
    AuthManager.getGmsToken(),
    true
  );
  try {
    return response?.data;
  } catch (e) {
    console.log(e, 'error');
    return null;
  }
};

// Copy UI Model
export const copyUIModelApi = async (
  configureId: string,
  modelId: string,
  formData: FormData
) => {
  const response = await ServiceWrapper.doPut(
    `${api_base_url}guide/${configureId}/model-ui/${modelId}/copy`,
    formData,
    null,
    AuthManager.getGmsToken(),
    true
  );
  try {
    return response?.data;
  } catch (e) {
    console.log(e, 'error');
    return null;
  }
};

// Get UI Model by ID
export const getUIModelById = async (configureId: string, modelId: string) => {
  try {
    const response = await ServiceWrapper.doGet(
      `${api_base_url}guide/${configureId}/model-ui/${modelId}`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.log('Error', error);
    return null;
  }
};

// Update UI Model metadata
export const updateUIModelApi = async (
  configureId: string,
  modelId: string,
  formData: FormData
) => {
  const response = await ServiceWrapper.doPut(
    `${api_base_url}guide/${configureId}/model-ui/${modelId}`,
    formData,
    null,
    AuthManager.getGmsToken(),
    true
  );
  try {
    return response?.data;
  } catch (e) {
    console.log(e, 'error');
    return null;
  }
};

// Update Header
export const updateHeader = async (
  configureId: string,
  modelId: string,
  payload: any
) => {
  try {
    let data = payload;

    if (!(payload instanceof FormData)) {
      const jsonString = JSON.stringify(payload);
      const parsedPayload = JSON.parse(jsonString);
      data = parsedPayload;
    }

    const response = await ServiceWrapper.doPut(
      `${api_base_url}/guide/${configureId}/model-ui/${modelId}/header`,
      data,
      null,
      AuthManager.getGmsToken(),
      true
    );
    return response?.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

// Update Footer
export const updateFooter = async (
  configureId: string,
  modelId: string,
  payload: any
) => {
  try {
    let data = payload;

    if (!(payload instanceof FormData)) {
      const jsonString = JSON.stringify(payload);
      const parsedPayload = JSON.parse(jsonString);
      data = parsedPayload;
    }

    const response = await ServiceWrapper.doPut(
      `${api_base_url}/guide/${configureId}/model-ui/${modelId}/footer`,
      data,
      null,
      AuthManager.getGmsToken(),
      true
    );
    return response?.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

// Update Navigation
export const updateNavigation = async (
  configureId: string,
  modelId: string,
  payload: any
) => {
  try {
    const response = await ServiceWrapper.doPut(
      `${api_base_url}/guide/${configureId}/model-ui/${modelId}/navigation`,
      payload,
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.log(error);
  }
};

// Update Canvas
export const updateCanvas = async (
  configureId: string,
  modelId: string,
  canvasId: string,
  payload: any
) => {
  try {
    const response = await ServiceWrapper.doPut(
      `${api_base_url}/guide/${configureId}/model-ui/${modelId}/canvas/${canvasId}`,
      payload,
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.log(error);
  }
};

// Get Canvas
export const getCanvas = async (
  configureId: string,
  modelId: string,
  canvasId: string
) => {
  try {
    const response = await ServiceWrapper.doGet(
      `${api_base_url}/guide/${configureId}/model-ui/${modelId}/canvas/${canvasId}`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.log(error);
  }
};

// Get Custom CSS
export const getCustomCSS = async (projectId: string, modelId: string) => {
  const response = await ServiceWrapper.doGet(
    `${api_base_url}guide/${projectId}/model-ui/${modelId}/css`,
    null,
    null,
    AuthManager.getGmsToken()
  );
  try {
    return response?.data;
  } catch (error) {
    console.log('Error', error);
    return null;
  }
};

// Update Custom CSS
export const updateCustomCSS = async (
  projectId: string,
  modelId: string,
  data: any
) => {
  const response = await ServiceWrapper.doPut(
    `${api_base_url}guide/${projectId}/model-ui/${modelId}/css`,
    { customCss: data },
    null,
    AuthManager.getGmsToken()
  );
  try {
    return response?.data;
  } catch (error) {
    console.log('Error', error);
    return null;
  }
};

// Get Theme
export const getThemeAPI = async (projectId: string, modelId: string) => {
  const response = await ServiceWrapper.doGet(
    `${api_base_url}guide/${projectId}/model-ui/${modelId}/theme`,
    null,
    null,
    AuthManager.getGmsToken()
  );
  try {
    return response?.data;
  } catch (error) {
    console.log('Error', error);
    return null;
  }
};

// Update Theme
export const updateThemeAPI = async (
  projectId: string,
  modelId: string,
  data: any
) => {
  const response = await ServiceWrapper.doPut(
    `${api_base_url}guide/${projectId}/model-ui/${modelId}/theme`,
    { theme: data },
    null,
    AuthManager.getGmsToken()
  );
  try {
    return response?.data;
  } catch (error) {
    console.log('Error', error);
    return null;
  }
};

// Get Shared Widgets
export const getSharedWidgetsAPI = async (projectId: string, modelId: string) => {
  const response = await ServiceWrapper.doGet(
    `${api_base_url}guide/${projectId}/model-ui/${modelId}/shared-widgets`,
    null,
    null,
    AuthManager.getGmsToken()
  );
  try {
    return response?.data;
  } catch (error) {
    console.log('Error fetching shared widgets:', error);
    return null;
  }
};

// Update Shared Widgets
export const updateSharedWidgetsAPI = async (
  projectId: string,
  modelId: string,
  data: any
) => {
  const response = await ServiceWrapper.doPut(
    `${api_base_url}guide/${projectId}/model-ui/${modelId}/shared-widgets`,
    data,
    null,
    AuthManager.getGmsToken()
  );
  try {
    return response?.data;
  } catch (error) {
    console.log('Error updating shared widgets:', error);
    return null;
  }
};

// Image/Icon Upload
export const imageIconUploadAPI = async (
  projectId: string,
  modelId: string,
  file: File,
  imageType: string = 'icon'
) => {
  try {
    const formData = new FormData();
    formData.append('imageType', imageType);
    formData.append('image', file);

    const response = await ServiceWrapper.doPost(
      `${api_base_url}guide/${projectId}/model-ui/${modelId}/upload-image`,
      formData,
      null,
      AuthManager.getGmsToken(),
      true
    );
    return response?.data;
  } catch (error) {
    console.log('Error', error);
    return null;
  }
};

// Get Products by Category
export const getProductsByCategory = async (
  industryId: string,
  categoryId: string
) => {
  try {
    const response = await ServiceWrapper.doGet(
      `${api_base_url}guide/${industryId}/${categoryId}/products`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.log('Error', error);
    return null;
  }
};
